// File: frontend/src/services/api.js
// Axios configuration for API calls

import axios from 'axios';

// Create axios instance with dynamic baseURL
const getBaseURL = () => {
  // In production (served from same domain), use relative API path
  if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
    return '/api';
  }
  // In development, frontend runs on port 5173+ (Vite auto-assigns), backend on port 3000
  // Direct connection to backend since proxy might not be configured
  if (window.location.port.startsWith('517') || window.location.port.startsWith('518')) {
    return 'http://localhost:3000/api'; // Direct connection to backend
  }
  // Legacy support for other dev ports
  if (window.location.port === '4173') {
    return 'http://localhost:3000/api';
  }
  // If running from same port as backend, use relative API path
  if (window.location.port === '3000') {
    return '/api';
  }
  // Default: point to backend on port 3000
  return 'http://localhost:3000/api';
};

const api = axios.create({
  baseURL: getBaseURL(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token.
// SECURITY: never log the token (or any preview of it) to the browser console.
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('wa-crm-token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Exclude auth endpoints: hydration (/auth/me) and login (/auth/login)
      // own their own clean logout flow. Hard-redirecting here would double up
      // with verifyToken's LOGOUT and stomp on the login form's error handling.
      const reqUrl = error.config?.url || '';
      const isAuthEndpoint = reqUrl.includes('/auth/me') || reqUrl.includes('/auth/login');

      if (!isAuthEndpoint) {
        // Token expired, redirect to login
        localStorage.removeItem('wa-crm-token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ===================================
// USER MANAGEMENT API FUNCTIONS
// ===================================

// Get users with filters
export const getUsers = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.search) params.append('search', filters.search);
  if (filters.role) params.append('role', filters.role);
  if (filters.active !== undefined) params.append('active', filters.active);
  if (filters.limit) params.append('limit', filters.limit);
  
  const response = await api.get(`/users?${params.toString()}`);
  return response.data;
};

// Get user by ID
export const getUserById = async (id) => {
  const response = await api.get(`/users/${id}`);
  return response.data;
};

// Create new user
export const createUser = async (userData) => {
  const response = await api.post('/users', userData);
  return response.data;
};

// Update user
export const updateUser = async (id, userData) => {
  const response = await api.put(`/users/${id}`, userData);
  return response.data;
};

// Delete user
export const deleteUser = async (id) => {
  const response = await api.delete(`/users/${id}`);
  return response.data;
};

// Get available roles
export const getRoles = async () => {
  const response = await api.get('/users/get-roles');
  return response.data;
};

// Update user status (activate/deactivate)
export const updateUserStatus = async (id, isActive) => {
  const response = await api.patch(`/users/${id}/status`, { is_active: isActive });
  return response.data;
};

// ===================================
// SETTINGS API FUNCTIONS
// ===================================

// System Settings
export const getSystemSettings = async () => {
  const response = await api.get('/settings/system');
  return response.data;
};

export const getSystemSetting = async (key) => {
  const response = await api.get(`/settings/system/${key}`);
  return response.data;
};

export const updateSystemSetting = async (key, value) => {
  const response = await api.put(`/settings/system/${key}`, { value });
  return response.data;
};

// User Settings
export const getUserSettings = async () => {
  const response = await api.get('/settings/user');
  return response.data;
};

export const getUserSetting = async (key) => {
  const response = await api.get(`/settings/user/${key}`);
  return response.data;
};

export const updateUserSetting = async (key, value) => {
  const response = await api.put(`/settings/user/${key}`, { value });
  return response.data;
};

// Pipeline Settings
export const getPipelineStages = async () => {
  const response = await api.get('/settings/pipeline');
  return response.data;
};

export const createPipelineStage = async (stageData) => {
  const response = await api.post('/settings/pipeline', stageData);
  return response.data;
};

export const updatePipelineStage = async (id, stageData) => {
  const response = await api.put(`/settings/pipeline/${id}`, stageData);
  return response.data;
};

export const deletePipelineStage = async (id) => {
  const response = await api.delete(`/settings/pipeline/${id}`);
  return response.data;
};

// Configuration Export/Import
export const exportConfiguration = async () => {
  const response = await api.get('/settings/config/export');
  return response.data;
};

export const importConfiguration = async (config) => {
  const response = await api.post('/settings/config/import', { config });
  return response.data;
};

// ===================================
// ANALYTICS API FUNCTIONS
// ===================================

// Get dashboard analytics
export const getAnalyticsDashboard = async (dateRange = 30) => {
  const response = await api.get(`/analytics/dashboard?date_range=${dateRange}`);
  return response.data;
};

// Get pipeline analytics
export const getPipelineAnalytics = async (dateRange = 30) => {
  const response = await api.get(`/analytics/pipeline?date_range=${dateRange}`);
  return response.data;
};

// Get conversion analytics
export const getConversionAnalytics = async (dateRange = 30) => {
  const response = await api.get(`/analytics/conversion?date_range=${dateRange}`);
  return response.data;
};

// Get performance analytics
export const getPerformanceAnalytics = async (dateRange = 30) => {
  const response = await api.get(`/analytics/performance?date_range=${dateRange}`);
  return response.data;
};

// SDR — entrevistas del contacto
export const getContactSdrSessions = async (id) => {
  const response = await api.get(`/contacts/${id}/sdr-sessions`);
  return response.data.data; // array de sesiones con transcript anidado
};

// Timeline unificado de la oportunidad (bitácora local rápida)
export const getOpportunityTimeline = (id) =>
  api.get(`/opportunities/${id}/timeline`).then((r) => r.data.data);

// Reuniones del lead vía RAG (best-effort, async, falla suave)
export const getOpportunityMeetingsRag = (id) =>
  api.get(`/opportunities/${id}/meetings-rag`, { timeout: 180000 }).then((r) => r.data);

// ===================================
// MEETINGS REVIEW API FUNCTIONS
// ===================================

// Get meetings pending manual link to an opportunity
export const getMeetingsReview = () =>
  api.get('/meetings/review').then((r) => r.data);

// Link a meeting to an opportunity
export const linkMeeting = (id, opportunityId) =>
  api.post(`/meetings/${id}/link`, { opportunity_id: opportunityId }).then((r) => r.data);

export default api;